import { diagLog, webLog } from './webLogger';
import { perf } from '../../lib/perfLogger';
import type { RevitOperationProgressSnapshot } from './operationProgressSnapshotMapper';
import {
  getRevitProgressRequestTimeoutMs,
  type RevitProgressRequestWorkload,
} from '../../lib/revit/revitOperationTimeoutPolicy';

export type RevitOperationPollOptions<T> = {
  intervalMs?: number;
  maxRevitWaitMs?: number;
  timeoutMs?: number;
  workload?: RevitProgressRequestWorkload;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  parseResult: (resultJson: string) => T;
};

interface OperationProgressReadResult {
  success: boolean;
  progress?: RevitOperationProgressSnapshot;
  error?: string;
}

interface OperationResultReadResult {
  success: boolean;
  resultReady?: boolean;
  resultJson?: string;
  error?: string;
}

export interface OperationPollerDependencies {
  getProgress: (
    operationId: string,
    requestTimeoutMs: number
  ) => Promise<OperationProgressReadResult>;
  getResult: (operationId: string) => Promise<OperationResultReadResult>;
  resetTransport: () => void;
  formatProgress: (progress: RevitOperationProgressSnapshot) => string;
  getAcceptanceTimeoutError: (
    progress: RevitOperationProgressSnapshot,
    maxRevitWaitMs?: number
  ) => string | undefined;
}

const TerminalResultReadAttempts = 3;

async function readTerminalResultWithRecovery(
  operationId: string,
  retryDelayMs: number,
  dependencies: OperationPollerDependencies
): Promise<OperationResultReadResult> {
  let lastResult: OperationResultReadResult = {
    success: false,
    error: 'Operation result failed',
  };

  for (let attempt = 1; attempt <= TerminalResultReadAttempts; attempt += 1) {
    lastResult = await dependencies.getResult(operationId);
    if (lastResult.success) return lastResult;

    // The plugin retains terminal payloads for ten minutes, so a transport
    // failure here is safe to retry without rerunning the Revit mutation.
    dependencies.resetTransport();
    webLog.warn(
      'operationProgressClient',
      `result read failed operationId=${operationId} attempt=${attempt} error=${lastResult.error || ''}`
    );
    if (attempt < TerminalResultReadAttempts) {
      await new Promise((resolve) => globalThis.setTimeout(resolve, retryDelayMs));
    }
  }

  return lastResult;
}

const progressReportsFamilyCount = (
  progress: RevitOperationProgressSnapshot | undefined
): boolean => Boolean(
  progress && (
    progress.operationType === 'quickfill.load' ||
    progress.operationType === 'family.snapshot' ||
    progress.functionName.includes('FamilyParameterSnapshotCollector')
  )
);

export function resolveProgressRequestTimeoutMs(
  progress: RevitOperationProgressSnapshot | undefined,
  workload: RevitProgressRequestWorkload = {}
): number {
  const observedFamilyCount = progressReportsFamilyCount(progress)
    ? progress?.total
    : undefined;
  return getRevitProgressRequestTimeoutMs({
    familyCount: Math.max(workload.familyCount ?? 0, observedFamilyCount ?? 0),
    parameterCount: workload.parameterCount,
  });
}

function getPublicationSignature(progress: RevitOperationProgressSnapshot): string {
  const itemCounts = new Map<string, number>();
  progress.items.forEach((item) => {
    itemCounts.set(item.status, (itemCounts.get(item.status) ?? 0) + 1);
  });
  return [
    progress.phase,
    progress.state ?? '',
    progress.processed ?? '',
    progress.total ?? '',
    progress.failed ? 'failed' : 'ok',
    progress.resultReady ? 'terminal' : 'running',
    progress.error ?? '',
    Array.from(itemCounts.entries()).sort().map(([key, count]) => `${key}:${count}`).join('|'),
  ].join('::');
}

function getLogTransitionSignature(progress: RevitOperationProgressSnapshot): string {
  return [
    progress.phase,
    progress.state ?? '',
    progress.failed ? 'failed' : 'ok',
    progress.resultReady ? 'terminal' : 'running',
    progress.error ?? '',
  ].join('::');
}

export function createProgressPublicationGate(
  now: () => number = () => Date.now(),
  intervalMs = 1_000
): (progress: RevitOperationProgressSnapshot) => boolean {
  let lastSignature = '';
  let lastPublishedAt = Number.NEGATIVE_INFINITY;
  return (progress) => {
    const signature = getPublicationSignature(progress);
    const checkedAt = now();
    if (signature === lastSignature && checkedAt - lastPublishedAt < intervalMs) return false;
    lastSignature = signature;
    lastPublishedAt = checkedAt;
    return true;
  };
}

export function createProgressLogGate(
  now: () => number = () => Date.now(),
  aggregateIntervalMs = 5_000
): (progress: RevitOperationProgressSnapshot) => boolean {
  let lastTransitionSignature = '';
  let lastLoggedAt = Number.NEGATIVE_INFINITY;
  return (progress) => {
    const transitionSignature = getLogTransitionSignature(progress);
    const checkedAt = now();
    if (
      transitionSignature === lastTransitionSignature &&
      checkedAt - lastLoggedAt < aggregateIntervalMs
    ) return false;
    lastTransitionSignature = transitionSignature;
    lastLoggedAt = checkedAt;
    return true;
  };
}

export async function pollRevitOperationWithTransport<T>(
  operationId: string,
  options: RevitOperationPollOptions<T>,
  dependencies: OperationPollerDependencies
): Promise<{ success: boolean; result?: T; error?: string }> {
  const intervalMs = options.intervalMs ?? 350;
  const staleProgressTimeoutMs = options.timeoutMs;
  let consecutiveTransportFailures = 0;
  let lastBackendProgressAt = Date.now();
  let lastProgress: RevitOperationProgressSnapshot | undefined;
  let pollSequence = 0;
  const shouldPublishProgress = createProgressPublicationGate();
  const shouldLogProgress = createProgressLogGate();
  webLog.info('operationProgressClient', `operation started operationId=${operationId}`);
  diagLog('progress-watchdog', `poll:start operationId=${operationId}`);

  while (true) {
    pollSequence += 1;
    const requestTimeoutMs = resolveProgressRequestTimeoutMs(lastProgress, options.workload);
    diagLog(
      'progress-watchdog',
      `poll:request seq=${pollSequence} operationId=${operationId} requestTimeoutMs=${requestTimeoutMs}`
    );
    const progressResult = await dependencies.getProgress(operationId, requestTimeoutMs);
    const checkedAt = Date.now();
    if (!progressResult.success) {
      consecutiveTransportFailures += 1;
      webLog.warn('operationProgressClient', `progress failed operationId=${operationId} error=${progressResult.error || ''}`);
      if (consecutiveTransportFailures >= 3) {
        dependencies.resetTransport();
        consecutiveTransportFailures = 0;
        webLog.warn('operationProgressClient', `transport reset after repeated failures operationId=${operationId}`);
      }
    } else if (progressResult.progress) {
      const progress = progressResult.progress;
      consecutiveTransportFailures = 0;
      lastBackendProgressAt = checkedAt;
      lastProgress = progress;
      if (shouldLogProgress(progress)) {
        webLog.info('operationProgressClient', `progress ${dependencies.formatProgress(progress)}`);
      }
      if (shouldPublishProgress(progress)) {
        const callbackStartedAt = performance.now();
        options.onProgress?.(progress);
        perf.slow('RevitOperation', 'onProgress callback', callbackStartedAt, 25, {
          operationId,
          phase: progress.phase,
          processed: progress.processed,
          total: progress.total,
        });
      }
      const acceptanceError = dependencies.getAcceptanceTimeoutError(
        progress,
        options.maxRevitWaitMs
      );
      if (acceptanceError) return { success: false, error: acceptanceError };

      if (progress.failed) {
        if (progress.resultReady) {
          const failedResult = await readTerminalResultWithRecovery(
            operationId,
            intervalMs,
            dependencies
          );
          if (failedResult.resultReady && failedResult.resultJson) {
            return {
              success: false,
              result: options.parseResult(failedResult.resultJson),
              error: progress.error || failedResult.error || 'Operation failed',
            };
          }
        }
        return { success: false, error: progress.error || 'Operation failed' };
      }
      if (progress.resultReady) {
        const result = await readTerminalResultWithRecovery(
          operationId,
          intervalMs,
          dependencies
        );
        if (!result.success) return { success: false, error: result.error || 'Operation result failed' };
        if (!result.resultReady || !result.resultJson) {
          return { success: false, error: 'Operation reported ready but returned no result payload' };
        }
        const parseStartedAt = performance.now();
        const parsedResult = options.parseResult(result.resultJson);
        perf.slow('RevitOperation', 'parse operation result json', parseStartedAt, 50, {
          operationId,
          resultJsonLength: result.resultJson.length,
        });
        return { success: true, result: parsedResult };
      }
    }
    if (
      staleProgressTimeoutMs && staleProgressTimeoutMs > 0 &&
      checkedAt - lastBackendProgressAt >= staleProgressTimeoutMs
    ) {
      return {
        success: false,
        error: `Operation ${operationId} did not report backend progress for ${Math.round(staleProgressTimeoutMs / 1000)}s${lastProgress?.message ? ` (${lastProgress.message})` : ''}`,
      };
    }
    await new Promise((resolve) => globalThis.setTimeout(resolve, intervalMs));
  }
}
